import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchTicketLaborMinutes } from '@/lib/labor/ticketLabor';
// DTC-L: route productivity-metrics "now" anchor through clock.
import { clock } from '@/utils/clock';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import {
  resolveTechnicianCapacity,
  deriveCapacityWindowMetrics,
} from '@/utils/capacity/technicianCapacityState';
// DMC-D3-FINAL: canonical deleted-state helper. Mirrors public.is_ticket_deleted
// (SQL) from migration 20260508120000_dmc_d2_deleted_state_helpers.sql.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { isSwitchedAwayPause } from '@/utils/capacity/switchedAwayPause';
// Core-100 R4 wave 3 (CORE-C Rel): a denied read is not an empty one.
import {
  fetchPassportSectionReadAccess,
  SectionReadNotPermittedError,
} from '@/lib/passport/sectionReadAccess';

export interface MinuteProductivityMetrics {
  availableMinutes: number;
  bookedMinutes: number;
  wrenchingMinutes: number;
  pauseMinutes: number;
  /**
   * Wrenching ÷ available, or `null` where no capacity is KNOWN.
   *
   * CORE-AW §44: this returned 0 whenever `availableMinutes` was 0, and
   * `availableMinutes` is 0 when every technician's working hours are
   * unrecorded. "0% utilised" is a measurement — it says the shop worked none
   * of the time it had — and it was being printed from the absence of a
   * denominator. Where the denominator is unknown there is no percentage, and
   * `null` says so rather than picking the flattering end of the range.
   *
   * Partial capacity still yields a number, computed over KNOWN capacity only;
   * `capacityComplete` is false and the surface must disclose it.
   */
  utilizationPct: number | null;
  /** Booked ÷ available. `null` on the same terms as `utilizationPct`. */
  bookLoadPct: number | null;
  /** An observed fact: wrenching minus booked. Never capacity-dependent. */
  overUnderMinutes: number;
  /**
   * Available minus wrenching minus pause — capacity-dependent, so `null`
   * where no capacity is known. Zero idle is a claim about how the day was
   * spent, and it cannot be made from an unknown day length.
   */
  idleMinutes: number | null;
  dataSource: 'time_clock' | 'estimated';
  /**
   * CORE-AW §44: technician-days whose capacity could not be established
   * because no working hours are recorded. Those days contribute nothing to
   * `availableMinutes`, so a non-zero count here means every ratio below is
   * computed over a PARTIAL denominator and must not be presented as the
   * shop's measured productivity.
   */
  unknownCapacityDays: number;
  /** False when `unknownCapacityDays > 0`. */
  capacityComplete: boolean;
  /**
   * True when NO capacity could be established at all — every contributing
   * technician-day had unrecorded hours. Distinct from a shop with no
   * technicians and from an established zero, both of which are knowledge.
   */
  capacityUnknown: boolean;
}

interface UseMinuteProductivityOptions {
  shopId: string;
  technicianProfileId?: string; // undefined = shop-wide
  startDate: string; // ISO date (yyyy-MM-dd)
  endDate: string; // ISO date (yyyy-MM-dd)
  shopTimezone: string;
}

export function useMinuteProductivityMetrics(opts: UseMinuteProductivityOptions) {
  return useQuery({
    // IV-D-22 R75 FIX-1: include shopTimezone in the key. The query body
    // reads opts.shopTimezone at lines 34-35 to compute UTC bounds; omitting
    // it meant a shop-TZ change (admin-level config edit) kept the stale
    // cache, giving the new TZ's requests the old TZ's bounds.
    queryKey: ['minute-productivity', opts.shopId, opts.technicianProfileId, opts.startDate, opts.endDate, opts.shopTimezone],
    queryFn: async (): Promise<MinuteProductivityMetrics> => {
      const { shopId, technicianProfileId, startDate, endDate, shopTimezone } = opts;

      // R12-M-1: Convert shop-local day bounds to UTC for timestamptz comparisons
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // 1. Resolve technician IDs (shop-level tech IDs, not profile IDs)
      let techIds: string[] = [];
      let technicians: any[] = [];

      if (technicianProfileId) {
        // Core-100 R4 wave 3 (CORE-C Rel): RLS answers a read this viewer may not make with ZERO rows, and zero
        // employment rows used to become the empty metrics ("no time data") — a claim about the person made from a
        // fact about the viewer (e.g. a service writer, or a Connect recruiter). The server states the viewer's read
        // basis first; no basis is reported as not-permitted, and a failed access read throws like any other read.
        const access = await fetchPassportSectionReadAccess(technicianProfileId);
        if (!access.time) throw new SectionReadNotPermittedError('time');

        // Get the shop technician ID for this profile
        // Core-100 R4 (CORE-C): every read below throws on error. Each used to discard it, so a failed read
        // collapsed into the empty metrics and the Time Signature told the person "no data" about time it
        // could not read. A read that did not happen reaches the caller as a failure (isError), never as zero.
        const { data: employment, error: employmentError } = await supabase
          .from('technician_employment_history')
          .select('shop_technician_id, technician:technicians(*)')
          .eq('technician_profile_id', technicianProfileId);
        if (employmentError) throw employmentError;

        techIds = employment?.map(e => e.shop_technician_id).filter(Boolean) || [];
        technicians = employment?.map(e => e.technician).filter(Boolean) || [];
      } else {
        // Get all active technicians for the shop
        const { data: techs, error: techsError } = await supabase
          .from('technicians')
          .select('*')
          .eq('shop_id', shopId)
          .eq('employment_status', 'Active');
        if (techsError) throw techsError;

        technicians = techs || [];
        techIds = technicians.map(t => t.id);
      }

      if (techIds.length === 0) {
        return createEmptyMetrics('estimated');
      }

      // 2. Calculate available minutes
      // First try time_clock_entries, fallback to calculateTechCapacity
      let availableMinutes = 0;
      let dataSource: 'time_clock' | 'estimated' = 'estimated';
      /** Technician-days whose capacity could not be established. */
      let unknownCapacityDays = 0;

      const { data: clockEntries, error: clockError } = await supabase
        .from('time_clock_entries')
        .select('*')
        .eq('shop_id', shopId)
        .in('technician_id', techIds)
        .gte('clock_in_time', startUtc)
        .lte('clock_in_time', endUtc);
      if (clockError) throw clockError;

      if (clockEntries && clockEntries.length > 0) {
        // Use time clock data
        dataSource = 'time_clock';
        availableMinutes = clockEntries.reduce((sum, entry) => {
          if (!entry.clock_in_time || !entry.clock_out_time) return sum;
          const clockIn = new Date(entry.clock_in_time);
          const clockOut = new Date(entry.clock_out_time);
          const minutes = Math.round((clockOut.getTime() - clockIn.getTime()) / 1000 / 60);
          return sum + Math.max(0, minutes);
        }, 0);
      } else {
        // Fallback to calculated capacity from shifts
        const days = eachDayOfInterval({
          start: parseISO(startDate),
          end: parseISO(endDate)
        });

        // CORE-AW §44. An unknown hours envelope used to add 0 here, which
        // made the denominator of every productivity ratio quietly smaller
        // than the shop and presented the result as measured. Unknown days
        // are counted instead, so the caller can say the figure is partial
        // rather than implying it is complete.
        for (const tech of technicians) {
          for (const day of days) {
            const capacity = resolveTechnicianCapacity(tech, day);
            if (capacity.state === 'unknown') { unknownCapacityDays += 1; continue; }
            availableMinutes += capacity.minutes;
          }
        }
      }

      // 3. Get completed tickets for booked & wrenching minutes
      const { data: completedTickets, error: completedError } = await supabase
        .from('tickets')
        .select('id')
        .eq('shop_id', shopId)
        .in('completed_by_tech_id', techIds)
        .eq('status', 'Completed')
        // CSI-S1: exclude soft-deleted Completed tickets from outer productivity
        // query. Mirrors the inner pause-tickets query's DMC-D3-FINAL guard so
        // both halves of the hook agree on the active-Completed set.
        .is('deleted_at', null)
        .gte('completed_at', startUtc)
        .lte('completed_at', endUtc);
      if (completedError) throw completedError;

      // oct20e LABOR (FD-2026-10-24-B, row 6a): booked = APPROVED labor of the completed work, never
      // tickets.predicted_minutes (the scheduling duration, which pushback / rollover / projection edits move).
      const approvedByTicket = await fetchTicketLaborMinutes((completedTickets || []).map((t) => t.id));
      const bookedMinutes = (completedTickets || []).reduce(
        (sum, t) => sum + (approvedByTicket.get(t.id)?.approvedLaborMinutes ?? 0),
        0
      );

      // 4. Get wrenching minutes from timer sessions
      const { data: timerSessions, error: timerError } = await supabase
        .from('ticket_timer_sessions')
        .select('duration_min')
        .eq('shop_id', shopId)
        .in('technician_id', techIds)
        .gte('started_at', startUtc)
        .lte('started_at', endUtc)
        .not('duration_min', 'is', null);
      if (timerError) throw timerError;

      const wrenchingMinutes = (timerSessions || []).reduce(
        (sum, s) => sum + (s.duration_min || 0),
        0
      );

      // 5. Get pause minutes from pause sessions
      let pauseQuery = supabase
        .from('ticket_pause_sessions')
        // GAPS-4: pause_intent is read so a SWITCHED pause (hands-on on another vehicle) is not counted twice.
        .select('ticket_id, duration_minutes, paused_at, resumed_at, pause_intent')
        .eq('shop_id', shopId);
      // POLISH-1: a technician-scoped Time Signature subtracts only THAT technician's pauses. The query was
      // shop-wide, so one technician's idle time was reduced by every other technician's pauses. Shop-wide mode
      // keeps reading every pause in the shop (incl. legacy rows with no technician_id), unchanged.
      if (technicianProfileId) pauseQuery = pauseQuery.in('technician_id', techIds);
      const { data: pauseSessionRows, error: pauseError } = await pauseQuery
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc);
      if (pauseError) throw pauseError;
      // GAPS-4: a SWITCHED pause overlaps hands-on time on the other vehicle, which is already in wrenchingMinutes;
      // counting it as pause time too understated idle by the length of every switch. Not pause, not idle.
      const pauseSessions = ((pauseSessionRows ?? []) as unknown as Array<{ ticket_id: string; duration_minutes: number | null; paused_at: string; resumed_at: string | null; pause_intent?: string | null }>)
        .filter((p) => !isSwitchedAwayPause(p));

      // Get ticket status to exclude completed/deleted tickets and parking lot tickets with open pauses
      let pauseMinutes = 0;
      if (pauseSessions && pauseSessions.length > 0) {
        const pauseTicketIds = [...new Set(pauseSessions.map(p => p.ticket_id))];
        // DMC-D3-FINAL: project deleted_at so the canonical helper has full info.
        const { data: pauseTickets, error: pauseTicketsError } = await supabase
          .from('tickets')
          .select('id, parking_lot, status, deleted_at')
          .in('id', pauseTicketIds);
        if (pauseTicketsError) throw pauseTicketsError;

        const ticketDataMap = new Map(
          pauseTickets?.map(t => [
            t.id,
            { parking_lot: t.parking_lot, status: t.status, deleted_at: t.deleted_at },
          ]) || [],
        );

        pauseMinutes = pauseSessions.reduce((sum, p) => {
          const ticketData = ticketDataMap.get(p.ticket_id);

          // Skip pause sessions from completed or deleted tickets entirely.
          // DMC-D3-FINAL: deletion check via canonical helper (both reps).
          if (ticketData?.status === 'Completed' || isTicketDeleted(ticketData ?? {})) {
            // Only count closed (completed) pause sessions, not open ones
            if (p.duration_minutes && p.resumed_at) {
              return sum + p.duration_minutes;
            }
            return sum; // Skip unclosed pauses on completed tickets
          }
          
          // Completed pause sessions always count
          if (p.duration_minutes) {
            return sum + p.duration_minutes;
          }
          // For active pauses, exclude if ticket is in parking lot
          if (p.paused_at && !p.resumed_at) {
            const isInParkingLot = ticketData?.parking_lot === true;
            if (isInParkingLot) return sum; // Skip parking lot tickets
            
            const pausedAt = new Date(p.paused_at);
            const now = clock.now();
            const activeMinutes = Math.round((now.getTime() - pausedAt.getTime()) / 1000 / 60);
            return sum + Math.max(0, activeMinutes);
          }
          return sum;
        }, 0);
      }

      // 6. Compute derived metrics
      //
      // CORE-AW §44. `availableMinutes > 0 ? … : 0` made every
      // capacity-dependent figure read 0 when the denominator was missing,
      // which is exactly the case where nothing about it is known. The rule
      // lives in the capacity authority so it is provable in one place and
      // cannot drift from the states it depends on.
      const derived = deriveCapacityWindowMetrics({
        availableMinutes, wrenchingMinutes, bookedMinutes, pauseMinutes, unknownCapacityDays,
      });
      const { utilizationPct, bookLoadPct, overUnderMinutes, capacityUnknown } = derived;
      const idleMinutes = derived.idleMinutes;

      return {
        availableMinutes,
        bookedMinutes,
        wrenchingMinutes,
        pauseMinutes,
        utilizationPct,
        bookLoadPct,
        overUnderMinutes,
        idleMinutes,
        dataSource,
        unknownCapacityDays,
        capacityComplete: unknownCapacityDays === 0,
        capacityUnknown,
      };
    },
    enabled: Boolean(opts.shopId && opts.startDate && opts.endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

function createEmptyMetrics(dataSource: 'time_clock' | 'estimated'): MinuteProductivityMetrics {
  return {
    availableMinutes: 0,
    bookedMinutes: 0,
    wrenchingMinutes: 0,
    pauseMinutes: 0,
    utilizationPct: 0,
    bookLoadPct: 0,
    overUnderMinutes: 0,
    idleMinutes: 0,
    dataSource,
    unknownCapacityDays: 0,
    capacityComplete: true,
    // A shop with no technicians has a KNOWN capacity of nothing. That is not
    // the same as a shop whose technicians' hours were never recorded.
    capacityUnknown: false,
  };
}
